import React from "react";
import * as MUI from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import * as API_PATH from "../../../enums/ApiPaths";
import MyLoadingButton from "../../../components/buttons/MyLoadingButton";
import { ToggleDrawerContext } from "../../../contexts/ToggleDrawer";
import { useTranslation } from "react-i18next";
import { CREATE_PET } from "../../../enums/PermissionTitles";
import SimpleAlert from "../../../components/display/SimpleAlert";
import NewPetBody from "../../../components/form/NewPetBody";
import { useParams } from "react-router-dom";
import useUser, { useGetUserId } from "../../../hooks/useUser";
import { useFetch } from "../../../hooks/Fetching";
import useSubmit from "../../master/species/hooks/useSubmit";
import { PETS, PETS_DROP_DOWN } from "../../../enums/QueryKeys";
import useSubmitPet from "./hooks/useSubmitPet";
import { MUI_BUTTON_POSITIVE,MUI_BUTTON_NEGATIVE } from "../../../css/MuiClasses";
import ConfirmAndCancelButtons from "../../../components/buttons/ConfirmAndCancelButtons";

export default function AddPetForm({ mutationKey = [PETS, PETS_DROP_DOWN] }) {
  const { id: userId } = useParams();
  const loggedInuserId = useGetUserId();
  const { t, i18n } = useTranslation();
  const { currentClinic, roles } = useUser();
  const isMaster = roles.master;
  const apiPath = isMaster ? "/species/" : API_PATH.SPECIES_CLINIC_DTO;
  const {
    data,
    isLoading: fetchLoading,
    error,
  } = useFetch(apiPath, currentClinic?.id, "species");

  const allSpecies = isMaster ? data : data?.species || [];
  const [isOpen, setIsOpen] = React.useContext(ToggleDrawerContext);
  const methods = useForm();
  const [translatedSpecies, setTranslatedSpecies] = React.useState([]);
  const url = userId
    ? API_PATH.ADD_PET_TO_USER + userId
    : API_PATH.ADD_PET + loggedInuserId;
  const submit = useSubmitPet(url, mutationKey);

  const submitData = async () => {
    await submit.submitCallback(prepareObjectForSubmit());
    setIsOpen(false);
  };

  function prepareObjectForSubmit() {
    const pet = methods.watch();

    const new_obj = {
      ...pet,
      birthdate: isDateObj(pet.birthdate)
        ? pet.birthdate.format("DD/MM/YYYY")
        : pet.birthdate,
    };
    if (pet?.gender == undefined || pet?.gender == null) {
      new_obj.gender = "MALE";
    }
    return new_obj;
  }

  function isDateObj(date) {
    return typeof date === "object";
  }

  React.useEffect(() => {
    if (allSpecies) {
      const currentLang = i18n.language;
      const translation = allSpecies.map((species) => {
        if (species?.translations) {
          return {
            id: species.id,
            title: species?.translations[currentLang] || species.title,
            breed: species?.breed
          };
        } else {
          return {
            id: species.id,
            title: species.title,
            breed: species?.breed
          };
        }
      });
      setTranslatedSpecies(translation);
    }
  }, [allSpecies, i18n.language]);

  if (error || !translatedSpecies) {
    return <SimpleAlert severity={"error"} message={"ErrorLoadingSpecies"} />;
  }

  if (fetchLoading) {
    return <MUI.LinearProgress />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitData)}>
        <NewPetBody species={translatedSpecies} key={"new-pet-for-user"} testIdPrefix="pet.add-form" />
        <MUI.Grid container>
          <MUI.Grid item xs={12} marginTop={"30px"}>
            <ConfirmAndCancelButtons
              cancelTitle="Cancel"
              confirmTitle="Save"
              onCancel={() => setIsOpen()}
              isConfirmLoading={submit.loading}
              permissions={[CREATE_PET]}
              useCustomLoadingButtonOnConfirm={true}
              confirmButtonType="submit"
              testId="pet.add-form"
            />
          </MUI.Grid>
        </MUI.Grid>
      </form>
    </FormProvider>
  );
}
